import type { ExperienceBlockDraft, ResolvedVisual } from '@matter_extractor/shared';
import {
  ANIMATION_SPEED_MS,
  assertNever,
  isBlockDisplayed,
  resolveTheme,
} from '@matter_extractor/shared';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { BlockFrame } from './block-frame';
import { ButtonBlock } from './blocks/button-block';
import { CheckboxBlock } from './blocks/checkbox-block';
import { CopyableBlock } from './blocks/copyable-block';
import { ImageBlock } from './blocks/image-block';
import { InputBlock } from './blocks/input-block';
import { LinkBlock } from './blocks/link-block';
import { NoteBlock } from './blocks/note-block';
import { NoyauBlock } from './blocks/noyau-block';
import { SelectionBlock } from './blocks/selection-block';
import { SoundBlock } from './blocks/sound-block';
import { TextBlock } from './blocks/text-block';
import { type PageReaderProps, READER_TEST_IDS, type ReaderMode } from './contracts';
import {
  type BlockDisplayed,
  EVERY_BLOCK_SHOWN,
  advanceHint,
  buttonBehaviour,
  readerBlockers,
  readerText,
  readerTicked,
  tempsMasked,
  tempsStartDelayMs,
} from './reader-play';
import { type ReaderMotion, asStyle } from './reader-theme';

/**
 * Internal props are `T | undefined` rather than `?:` on purpose: with
 * `exactOptionalPropertyTypes`, an optional prop cannot be handed an explicit
 * `undefined`, and a component passing five of those through would be five
 * conditional spreads. The PUBLIC shape, in `contracts.ts`, keeps `?:`.
 */
export interface ReaderBodyProps {
  readonly page: PageReaderProps['page'];
  readonly theme: PageReaderProps['theme'];
  readonly mode: ReaderMode;
  readonly motion: ReaderMotion;
  /** Whether the temps play at all - `page-reader.tsx` decides, this obeys. */
  readonly play: boolean;
  /** The person has touched the page: a Son may start (§9.2). */
  readonly gestured: boolean;
  readonly values: PageReaderProps['values'];
  readonly selectedBlockId: string | null;
  readonly onSelectBlock: ((blockId: string) => void) | undefined;
  readonly onValueChange: PageReaderProps['onValueChange'];
  readonly onAdvance: PageReaderProps['onAdvance'];
  readonly resolveVariable: PageReaderProps['resolveVariable'];
  readonly variableNames: PageReaderProps['variableNames'];
  /** Controlled: how many temps are shown. `undefined` - the count is this component's. */
  readonly revealed: number | undefined;
  readonly onRevealed: ((revealed: number) => void) | undefined;
  readonly conditions: PageReaderProps['conditions'];
  readonly conditionMark: PageReaderProps['conditionMark'];
  readonly noyau: PageReaderProps['noyau'];
  /** The Bouton whose advance waits - its request, or the noyau - or null (noyau.md §4.6). */
  readonly waitingBlockId: string | null;
}

/**
 * The temps, the blocks, and what a press does (§9.2, §9.3).
 *
 * Mounted by `page-reader.tsx` with a `key` that carries the page and the
 * replay, so « rejouer l'apparition » is a REMOUNT - the repository's own idiom
 * for « recharger cet état » (the versioned editor, the two Réglages sections).
 * That is what makes a temps which had already appeared animate again, and it
 * is why nothing here repairs state in an effect.
 *
 * What is NOT state: how many temps are shown when nothing plays. It is
 * `page.temps.length`, derived at every render, so a temps added in the editor
 * appears at once instead of waiting for a remount nobody asked for.
 *
 * ## `revealed` may be somebody else's (issue #141)
 *
 * The count below is a controlled / uncontrolled PAIR. `revealed` absent - the
 * three mounts that existed before the full-screen preview - it is this
 * component's `useState`, and every line reads the same as it did. `revealed`
 * given, the prop IS the count and `reveal` asks the caller for a new one
 * instead of setting state; the two places that want one more temps - the delay
 * of §9.3 running out, a Bouton `next-temps` pressed - go through that single
 * function, so neither of them knows which régime is in force.
 *
 * The caller is reached through a REF, for the reason `advance` already is: an
 * `onRevealed` written as an arrow in JSX is a new function at every render,
 * and a timer that depended on its identity would be cleared and restarted on
 * every render - a temps set to appear after a second would never appear.
 *
 * ## A masked block does not exist for the page (§9.2, H70, H72)
 *
 * `conditions` given, `isBlockDisplayed` of `shared` decides which blocks are
 * there - the function the server plays at the advance. A masked block is not
 * drawn, does not animate and greys no button; a temps every block of which is
 * masked is not drawn either, and is skipped with its delay (`tempsMasked`,
 * `tempsStartDelayMs`). The COUNT of temps is untouched: a masked temps is still
 * a temps, which keeps `buttonBehaviour` and the server's `revealedTempsCount`
 * reading the same indices.
 */
export function ReaderBody({
  page,
  theme,
  mode,
  motion,
  play,
  gestured,
  values,
  selectedBlockId,
  onSelectBlock,
  onValueChange,
  onAdvance,
  resolveVariable,
  variableNames,
  revealed,
  onRevealed,
  conditions,
  conditionMark,
  noyau,
  waitingBlockId,
}: ReaderBodyProps): ReactNode {
  const total = page.temps.length;
  const [ownPlayed, setOwnPlayed] = useState(1);
  const played = revealed ?? ownPlayed;
  const shown = play ? Math.min(played, total) : total;

  /*
   * The callback is reached through a ref so the two timers below do not depend
   * on its identity. A caller that rebuilds `onAdvance` at each render - the
   * normal shape of an arrow written in JSX - would otherwise restart the
   * automatic duration on every render, and a page set to advance after ten
   * seconds would never advance.
   */
  const advance = useRef(onAdvance);
  useEffect(() => {
    advance.current = onAdvance;
  });

  /** The ONE spelling of « un temps de plus », whichever side holds the count. */
  const ask = useRef(onRevealed);
  useEffect(() => {
    ask.current = onRevealed;
  });
  const reveal = useCallback(
    (atLeast: number): void => {
      if (revealed !== undefined) {
        ask.current?.(Math.max(revealed, atLeast));
        return;
      }
      setOwnPlayed((current) => Math.max(current, atLeast));
    },
    [revealed],
  );

  /* The next temps, after the previous one's animations plus its own delay
   * (§9.3). `null` when it waits for a click instead: its button reveals it.
   *
   * The wait is computed OUTSIDE the effect so the effect depends on two
   * numbers rather than on `page.temps`. A caller that hands a fresh page
   * object at each render - an editor rebuilding its draft - would otherwise
   * clear and restart the timer on every keystroke, and a temps set to appear
   * after a second would never appear. */
  const displayed: BlockDisplayed =
    conditions === undefined ? EVERY_BLOCK_SHOWN : (block) => isBlockDisplayed(block, conditions);
  const wait =
    play && shown < total ? tempsStartDelayMs(page.temps, shown, motion, displayed) : null;
  useEffect(() => {
    if (wait === null) return;
    const timer = window.setTimeout(() => reveal(shown + 1), wait);
    return () => window.clearTimeout(timer);
  }, [wait, shown, reveal]);

  /* « Une durée facultative qui avance seule » (§9.2), for an intertitre. It
   * runs from the page and not from the last temps: H27 has a duration shorter
   * than the last delay SIGNALLED by the editor and never refused, so the
   * reader has to be able to cut a page short. */
  const duration = page.autoDurationSeconds;
  useEffect(() => {
    if (mode !== 'interactive' || duration === null) return;
    const timer = window.setTimeout(
      () => advance.current?.({ reason: 'duration', block: null }),
      duration * 1000,
    );
    return () => window.clearTimeout(timer);
  }, [mode, duration]);

  const blockers = readerBlockers(page, shown, values, displayed);
  const blocked = blockers.length > 0;
  const hint = advanceHint(blockers);

  /* A block's number is its place in the WHOLE page, so it does not shift as
   * temps appear - it is what names the selection target at the keyboard. */
  const positions = new Map<string, number>();
  page.temps
    .flatMap((temps) => temps.blocks)
    .forEach((block, index) => positions.set(block.id, index + 1));

  /* ONE sentence per page and not one per button: a page with several buttons
   * is a page of CHOICES (§8.6), and three copies of « Cochez la case » would
   * crowd the choice rather than answer it. The first button that is still
   * THERE - a spent `next-temps` button is gone, and the sentence with it -
   * or the « Choisir ce texte » of a Noyau block, which is a way out too. */
  const firstButtonId =
    page.temps
      .slice(0, shown)
      .flatMap((temps) => temps.blocks)
      .find(
        (block) =>
          (block.type === 'button' &&
            displayed(block) &&
            buttonBehaviour(page, block.id, block.action, shown).kind !== 'spent') ||
          (block.type === 'noyau' && block.mode !== 'portrait'),
      )?.id ?? null;

  const press = (block: Extract<ExperienceBlockDraft, { type: 'button' }>): void => {
    const behaviour = buttonBehaviour(page, block.id, block.action, shown);
    if (behaviour.kind === 'spent') return;
    if (behaviour.kind === 'next-temps') {
      reveal(behaviour.tempsIndex + 1);
      return;
    }
    advance.current?.({ reason: 'button', block });
  };

  /** « Choisir ce texte » (noyau.md §5.1): the card confirmed leaves the page. */
  const confirm = (block: Extract<ExperienceBlockDraft, { type: 'noyau' }>, cardId: string) => {
    if (mode !== 'interactive') return;
    advance.current?.({ reason: 'noyau', block, cardId });
  };

  const change = (blockId: string, value: string | boolean): void => {
    if (mode !== 'interactive') return;
    onValueChange?.(blockId, value);
  };

  /* A page of CHOICE takes the wider column of its format (issue #168; the
     maquette `ChoixEcran`), read off EVERY temps and not the ones shown: a
     column that widened when its cards appear would reflow the consigne under
     the eyes of the person reading it. */
  const choice = page.temps.some((one) =>
    one.blocks.some((block) => block.type === 'noyau' && block.mode !== 'portrait'),
  );

  return (
    <div className="lecteur-colonne" {...(choice ? { 'data-column': 'choice' } : {})}>
      {page.temps.slice(0, shown).map((temps, tempsIndex) =>
        /* A masked temps draws nothing (H70): not even the group, whose first
           child would otherwise decide the rhythm of the column's first block. */
        tempsMasked(temps, displayed) ? null : (
          <div
            key={temps.id}
            className="lecteur-temps"
            data-testid={READER_TEST_IDS.temps}
            data-temps-index={tempsIndex}
            style={asStyle({ '--lecteur-anim-ms': `${ANIMATION_SPEED_MS[temps.speed]}ms` })}
          >
            {temps.blocks.map((block) => {
              if (!displayed(block)) return null;
              if (
                block.type === 'button' &&
                buttonBehaviour(page, block.id, block.action, shown).kind === 'spent'
              ) {
                return null;
              }
              const visual = resolveTheme(theme, page.visual, block.visual);
              return (
                <BlockFrame
                  key={block.id}
                  block={block}
                  visual={visual}
                  mode={mode}
                  position={positions.get(block.id) ?? 0}
                  selected={selectedBlockId === block.id}
                  onSelectBlock={onSelectBlock}
                  conditionMark={conditionMark?.(block) ?? null}
                >
                  {blockContent({
                    block,
                    visual,
                    mode,
                    gestured,
                    blocked,
                    hint: block.id === firstButtonId ? hint : null,
                    waiting: block.id === waitingBlockId,
                    value: readerText(values, block.id),
                    ticked: readerTicked(values, block.id),
                    onPress: () => {
                      if (block.type === 'button') press(block);
                    },
                    onChange: (next) => change(block.id, next),
                    onConfirm: (cardId) => {
                      if (block.type === 'noyau') confirm(block, cardId);
                    },
                    resolveVariable,
                    variableNames,
                    noyau,
                  })}
                </BlockFrame>
              );
            })}
          </div>
        ),
      )}
    </div>
  );
}

type BlockContent = {
  readonly block: ExperienceBlockDraft;
  readonly visual: ResolvedVisual;
  readonly mode: ReaderMode;
  readonly gestured: boolean;
  readonly blocked: boolean;
  readonly hint: string | null;
  /** This Bouton's advance waits: its request, or the determination of the noyau (#174). */
  readonly waiting: boolean;
  readonly value: string;
  readonly ticked: boolean;
  readonly onPress: () => void;
  readonly onChange: (value: string | boolean) => void;
  /** « Choisir ce texte » on a Noyau block, with the card selected. */
  readonly onConfirm: (cardId: string) => void;
  readonly resolveVariable: PageReaderProps['resolveVariable'];
  readonly variableNames: PageReaderProps['variableNames'];
  readonly noyau: PageReaderProps['noyau'];
};

/**
 * The eleven types, dispatched.
 *
 * A function and not a component: it holds no state and calls no hook, and a
 * file whose whole body is a `switch` would be a file to open on the way to
 * every other one. `assertNever` is what keeps the eleven honest the day a
 * twelfth type is added - and it is what turned red on each of the two that
 * issue #137 added, on the Noyau block of #147 and on the Sélection and the Note
 * of #176, which is the whole point of writing it that way.
 */
function blockContent(content: BlockContent): ReactNode {
  const { block, visual, mode, resolveVariable, variableNames } = content;

  switch (block.type) {
    case 'text':
      return (
        <TextBlock block={block} resolveVariable={resolveVariable} variableNames={variableNames} />
      );
    case 'image':
      return <ImageBlock block={block} mode={mode} />;
    case 'sound':
      return <SoundBlock block={block} mode={mode} gestured={content.gestured} />;
    case 'button':
      return (
        <ButtonBlock
          block={block}
          visual={visual}
          mode={mode}
          blocked={content.blocked}
          hint={content.hint}
          waiting={content.waiting}
          onPress={content.onPress}
        />
      );
    case 'input':
      return (
        <InputBlock block={block} mode={mode} value={content.value} onChange={content.onChange} />
      );
    case 'checkbox':
      return (
        <CheckboxBlock
          block={block}
          mode={mode}
          checked={content.ticked}
          onChange={content.onChange}
          resolveVariable={resolveVariable}
          variableNames={variableNames}
        />
      );
    case 'link':
      return (
        <LinkBlock
          block={block}
          visual={visual}
          mode={mode}
          resolveVariable={resolveVariable}
          variableNames={variableNames}
        />
      );
    case 'copyable':
      return (
        <CopyableBlock
          block={block}
          mode={mode}
          resolveVariable={resolveVariable}
          variableNames={variableNames}
        />
      );
    case 'noyau':
      return (
        <NoyauBlock
          block={block}
          visual={visual}
          mode={mode}
          noyau={content.noyau}
          // The selection is the value the caller holds under the block's id.
          selected={content.value === '' ? null : content.value}
          blocked={content.blocked}
          hint={content.hint}
          onSelect={(cardId) => content.onChange(cardId)}
          onConfirm={content.onConfirm}
        />
      );
    case 'selection':
      return (
        <SelectionBlock
          block={block}
          mode={mode}
          value={content.value}
          onChange={content.onChange}
          resolveVariable={resolveVariable}
          variableNames={variableNames}
        />
      );
    case 'note':
      return (
        <NoteBlock
          block={block}
          mode={mode}
          value={content.value}
          onChange={content.onChange}
          resolveVariable={resolveVariable}
          variableNames={variableNames}
        />
      );
    default:
      return assertNever(block, 'reader block');
  }
}
